/**
 * SeoOverviewPage — `/seo` (B1, частичный). Cockpit: органика→MQL + гэпы + топ-задачи.
 * Полный (позиции/индексация/видимость) — Фаза 2 (после Вебмастер+GSC).
 */

import { usePageHeader } from '@/components/layout/pageHeader';
import { HelpButton } from '@/components/layout/HelpButton';
import { SEO_HELP } from '@/help/seoHelp';
import { LoadingBlock, ErrorBlock } from '@/shared/ui';
import { MetaBadges } from '@/components/seo/MetaBadges';
import { useSeoOverview } from '@/hooks/api/useSeo';

function num(v: number | null | undefined): string {
  return v == null ? '—' : new Intl.NumberFormat('ru-RU').format(v);
}

export function SeoOverviewPage() {
  usePageHeader({ title: 'SEO — Обзор', subtitle: 'Органика → MQL и вклад в пайплайн', actions: <HelpButton help={SEO_HELP.overview} /> });
  const { data, isLoading, isError, error, refetch } = useSeoOverview();

  const pages = data ? Object.values(data.stats.pagesByStatus).reduce((a, b) => a + b, 0) : 0;
  const allEmpty = !!data && pages === 0 && data.stats.tasksOpen === 0 && data.gapsCount === 0;

  return (
    <div className="col gap20" style={{ maxWidth: 1100 }}>
      {isLoading && <LoadingBlock label="Собираю обзор SEO…" />}
      {isError && <ErrorBlock text={error?.message} onRetry={() => void refetch()} />}

      {data && (
        <>
          <section className="stat-grid">
            <Stat label="Органические MQL (форма, 30д)" value={num(data.mql?.mqlForms ?? 0)} sub={`Яндекс ${num(data.mql?.byEngine.yandex ?? 0)} / Google ${num(data.mql?.byEngine.google ?? 0)}`} />
            <Stat label="Органик-сессии (30д)" value={num((data.traffic?.sessions.yandex ?? 0) + (data.traffic?.sessions.google ?? 0))} sub={`Яндекс ${num(data.traffic?.sessions.yandex ?? 0)} / Google ${num(data.traffic?.sessions.google ?? 0)}`} />
            <Stat label="Денежных кластеров без страницы" value={num(data.gapsCount)} sub="гэпы — список работ" />
            <Stat label="Задач в работу" value={num(data.stats.tasksOpen)} sub={`апрувнуто ${num(data.stats.tasksApproved)}`} />
          </section>

          {!data.metrikaConfigured && (
            <div className="card" style={{ borderLeft: '3px solid var(--warning, #CA8A04)' }}>
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                Metrika не подключена (нет YANDEX_METRIKA_TOKEN) — органические сессии и MQL недоступны.
              </p>
            </div>
          )}

          {allEmpty && (
            <div className="card" style={{ borderLeft: '3px solid var(--primary)' }}>
              <h3 style={{ marginTop: 0 }}>С чего начать</h3>
              <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                <li>«Семантика/карта» → <b>Собрать ядро (AI)</b> — получите BOFU-кластеры и гэп-лист.</li>
                <li>«Аудиты» → запустите <b>коммерческий аудит</b> лендинга — находки уйдут в задачи.</li>
                <li>«Органика → MQL» — уже показывает живые данные Metrika и стоимость заявки.</li>
              </ol>
            </div>
          )}

          <section className="card">
            <h3 style={{ marginTop: 0 }}>Что делать — топ-5 задач</h3>
            {data.topTasks.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>Бэклог пуст. Соберите ядро в «Семантика/карта» и запустите коммерческий аудит.</p>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Задача</th>
                    <th>Модуль</th>
                    <th>Метки</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topTasks.map((t) => (
                    <tr key={t.id}>
                      <td>{t.title}</td>
                      <td><span className="muted" style={{ fontSize: 12.5 }}>{t.source_module}</span></td>
                      <td><MetaBadges confidence={t.confidence} impact={t.impact} effort={t.effort} engine={t.engine} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value" style={{ fontSize: 26 }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
